import { i18n } from '@lingui/core';
import { Trans } from '@lingui/macro';
import IconTick from '@we-make-websites/ui-lib/icons/misc/tick.svg';
import clsx from 'clsx';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useRefinementList } from 'react-instantsearch';
import aa from 'search-insights';
import { useContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { algoliaProductIndex } from 'utils/algoliaConstants';
import type {
  ColorSwatchSetting,
  StoreFilterSetting,
} from '../globalContentContext/GlobalContentContext';
import { GlobalContentContext } from '../globalContentContext/GlobalContentContext';
import styles from './CustomRefinementList.module.scss';
import filterStyles from './Filters.module.scss';

/**
 * Custom refinement list component.
 * - Uses the same markup as the default RefinementList component, but uses the
 *   useRefinementList hook.
 */
export const CustomRefinementList = (
  props: UseRefinementListProps & {
    classes?: {
      container?: string;
      list?: string;
      item?: string;
    };
    filter: StoreFilterSetting;
  }
) => {
  const { swatchSettings } = useContext(GlobalContentContext);
  const { classes, filter } = props;
  const { items, refine, canRefine } = useRefinementList(props);
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);

  return (
    <div className={clsx(classes?.container, styles.refinementList)}>
      <ul className={classes?.list || styles.refinementList__list}>
        {items.map((item) => (
          <li
            key={item.value}
            className={classes?.item || styles.refinementList__item}
          >
            <button
              className={clsx(
                filterStyles.filters__itemButton,
                styles.refinementList__itemButton,
                filter.displaySwatches &&
                  filterStyles['filters__itemButton--swatches']
              )}
              aria-label={i18n._(
                /* i18n */
                'Collection page {value} filter button, filters products',
                {
                  value: item.label,
                }
              )}
              type="button"
              data-active={item.isRefined}
              onClick={() => {
                refine(item.value);
                aa('clickedFilters', {
                  index: algoliaProductIndex(locale),
                  eventName: 'Filters Clicked',
                  filters: [`${filter.searchParameter}:${item.value}`],
                });
              }}
            >
              <SwatchButtonContent
                item={item}
                showSwatches={filter.displaySwatches}
                swatchSettings={swatchSettings}
              />
            </button>
          </li>
        ))}

        {!canRefine && (
          <li className={classes?.item || styles.refinementList__item}>
            <span className="text-body-2-bold-mobile text-body-small-desktop">
              <Trans id="filters.noFilters">No filter options</Trans>
            </span>
          </li>
        )}
      </ul>
    </div>
  );
};

const SwatchButtonContent = ({
  item,
  showSwatches,
  swatchSettings,
}: {
  item: RefinementListItem;
  showSwatches?: boolean;
  swatchSettings?: ColorSwatchSetting[];
}) => {
  if (!showSwatches) {
    return (
      <>
        <IconTick />
        <span
          className={clsx(
            styles.refinementList__itemLabel,
            'text-body-2-bold-mobile text-body-small-desktop'
          )}
        >
          {item.label}
        </span>

        <span
          className={clsx(
            styles.refinementList__itemCount,
            'text-body-2-bold-mobile text-body-small-desktop'
          )}
        >
          ({item.count})
        </span>
      </>
    );
  }

  const itemSwatchData = swatchSettings?.find((swatch) => {
    const sanitizedItemLabel = item.label.toLowerCase();

    return swatch.colorNames.includes(sanitizedItemLabel);
  });

  return (
    <>
      <span className={styles.refinementList__swatchWrapper}>
        {itemSwatchData?.image && (
          <Image
            className={styles.refinementList__swatch}
            src={itemSwatchData.image}
            width={25}
            height={25}
            alt=""
          />
        )}
      </span>
      <span
        className={clsx(
          styles.refinementList__itemLabel,
          'text-body-2-bold-mobile text-body-small-desktop'
        )}
      >
        {item.label}
      </span>

      <span
        className={clsx(
          styles.refinementList__itemCount,
          'text-body-2-bold-mobile text-body-small-desktop'
        )}
      >
        ({item.count})
      </span>
    </>
  );
};
